import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  Box, Button, CircularProgress, Grid, IconButton, Paper, TableCell, TableContainer,
  TableRow, Tooltip, Table, TableHead, TableBody, Chip,
} from '@mui/material';
import { Edit, Delete } from '@mui/icons-material';
import SearchBox from '../Common/SearchBox';
import { getOpportunitiesByClient, deleteOpportunity, setStoreData } from '../../Redux/actions';
import ConfirmDialog from '../Common/ConfirmDialog';
import OpportunitySchedule from './OpportunitySchedule';
import { THEME_COLOR } from '../../theme/formStyles';

const subTabLinkStyle = (active) => ({
  padding: '8px 16px',
  backgroundColor: active ? THEME_COLOR : '#fff',
  color: active ? '#fff' : THEME_COLOR,
  border: `1px solid ${THEME_COLOR}`,
  fontWeight: 600,
  fontSize: '13px',
  textDecoration: 'none',
  display: 'inline-block',
  lineHeight: 1.5,
  cursor: 'pointer',
});

export default function Opportunity() {
  const { client_id, action } = useParams();
  const navigate = useNavigate();

  // Redirect bare /opportunity URL to /opportunity-list (same as calibration-report → calibration-list)
  useEffect(() => {
    if (!action) {
      navigate(`/client/${client_id}/settings/opportunity/opportunity-list`, { replace: true });
    }
  }, [action]);

  // Derive active tab from URL action param
  const activeTab = action === 'schedule-list' ? 'schedule' : 'list';

  return (
    <div>
      <div
        style={{
          display: 'flex',
          gap: '0px',
          marginTop: '16px',
          marginBottom: '8px',
          position: 'relative',
          zIndex: 2,
        }}
      >
        <button
          type="button"
          onClick={() => navigate(`/client/${client_id}/settings/opportunity/opportunity-list`)}
          style={{ ...subTabLinkStyle(activeTab === 'list'), borderRadius: '4px 0 0 4px' }}
        >
          OPPORTUNITY LIST
        </button>
        <button
          type="button"
          onClick={() => navigate(`/client/${client_id}/settings/opportunity/schedule-list`)}
          style={{
            ...subTabLinkStyle(activeTab === 'schedule'),
            borderLeft: 'none',
            borderRadius: '0 4px 4px 0',
          }}
        >
          BD CLIENT MEET SCHEDULE
        </button>
      </div>

      <div style={{ position: 'relative', zIndex: 1, paddingTop: '8px' }}>
        {activeTab === 'list' && <OpportunityList />}
        {activeTab === 'schedule' && <OpportunitySchedule />}
      </div>
    </div>
  );
}

function OpportunityList() {
  const { client_id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [queryParams, setQueryParams] = useSearchParams();
  const existingParams = Object.fromEntries([...queryParams]);

  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoveredRowId, setHoveredRowId] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const tableColumns = ['Sl.No.', 'Opportunity Name', 'Segment', 'BD Owner/SPOC', 'Stage', 'Created Date', ''];

  useEffect(() => {
    fetchOpportunities();
  }, [client_id, queryParams]);

  const fetchOpportunities = () => {
    setLoading(true);
    const search = existingParams?.search || '';
    dispatch(getOpportunitiesByClient(client_id))
      .then((res) => {
        if (res?.status === 200 && res.data?.success) {
          let data = res.data.data || [];
          if (search) {
            data = data.filter((opp) =>
              opp.opportunity_name?.toLowerCase().includes(search.toLowerCase())
            );
          }
          setOpportunities(data);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  const handleCreate = () => {
    navigate(`/client/${client_id}/settings/opportunity/create`);
  };

  const handleEdit = (id) => {
    navigate(`/client/${client_id}/settings/opportunity/edit/${id}`);
  };

  const handleDeleteClick = (item) => {
    setSelectedItem(item);
    setShowConfirmDialog(true);
  };

  const handleDeleteConfirm = () => {
    setShowConfirmDialog(false);
    dispatch(deleteOpportunity(selectedItem.id)).then((res) => {
      if (res?.status === 200 && res.data?.success) {
        dispatch(
          setStoreData('alertMessage', {
            msg: res.data?.message || 'Opportunity deleted successfully',
            type: 'success',
            handleClose: () => dispatch(setStoreData('alertMessage', null)),
          })
        );
        fetchOpportunities();
      } else {
        dispatch(
          setStoreData('alertMessage', {
            msg: res.data?.message || 'Failed to delete opportunity',
            type: 'fail',
            handleClose: () => dispatch(setStoreData('alertMessage', null)),
          })
        );
      }
    });
  };

  const handleSearch = (value) => {
    const params = new URLSearchParams(existingParams);
    if (value) {
      params.set('search', value);
    } else {
      params.delete('search');
    }
    navigate({ search: params.toString() }, { replace: true });
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'Qualified Lead':
      case 'Won':
        return { color: '#196a3e', bgcolor: '#f1faf4', border: '1px solid #c2e9cf' };
      case 'Feasibility Study':
        return { color: '#1a6894', bgcolor: '#f1f8fc', border: '1px solid #b8ddec' };
      case 'Proposal':
        return { color: '#8f6d1d', bgcolor: '#fffdee', border: '1px solid #eed699' };
      case 'Negotiation':
        return { color: '#a44d21', bgcolor: '#fff6ef', border: '1px solid #f4c1a5' };
      case 'Lost':
        return { color: '#c62828', bgcolor: '#ffebee', border: '1px solid #ffcdd2' };
      default:
        return { color: '#757575', bgcolor: '#f5f5f5', border: '1px solid #e0e0e0' };
    }
  };

  let tableContent;

  if (loading) {
    tableContent = (
      <TableRow>
        <TableCell colSpan={7} align="center">
          <CircularProgress sx={{ color: THEME_COLOR }} />
        </TableCell>
      </TableRow>
    );
  } else if (opportunities.length === 0) {
    tableContent = (
      <TableRow>
        <TableCell colSpan={7} align="center">
          <h5 className="w3-text-grey">No Data Found</h5>
        </TableCell>
      </TableRow>
    );
  } else {
    tableContent = opportunities.map((item, index) => (
      <TableRow
        key={item.id}
        onMouseEnter={() => setHoveredRowId(item.id)}
        onMouseLeave={() => setHoveredRowId(null)}
      >
        <TableCell>{index + 1}</TableCell>
        <TableCell>{item.opportunity_name}</TableCell>
        <TableCell>{item.segment || '-'}</TableCell>
        <TableCell>{item.bd_owners?.map((o) => o.name).join(', ') || '-'}</TableCell>
        <TableCell>
          <Chip
            label={item.status}
            size="small"
            sx={{ minWidth: '70px', fontWeight: 500, ...getStatusStyle(item.status) }}
          />
        </TableCell>
        <TableCell>{new Date(item.created_at).toLocaleDateString('en-GB')}</TableCell>
        <TableCell>
          <Box component="div" display="flex" justifyContent="right">
            <Tooltip title="Edit">
              <IconButton
                aria-label="edit"
                style={{ opacity: hoveredRowId === item.id ? 1 : 0, paddingLeft: '0px' }}
                onClick={() => handleEdit(item.id)}
              >
                <Edit className="cursor-pointer" style={{ color: 'black' }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                aria-label="delete"
                style={{ opacity: hoveredRowId === item.id ? 1 : 0, paddingLeft: '0px' }}
                onClick={() => handleDeleteClick(item)}
              >
                <Delete className="cursor-pointer" style={{ color: '#d32f2f' }} />
              </IconButton>
            </Tooltip>
          </Box>
        </TableCell>
      </TableRow>
    ));
  }

  return (
    <div>
      <Grid container>
        <Grid item md={12}>
          <Grid item container spacing={2} paddingY={2}>
            <Grid item md={6}>
              <Button
                style={{ backgroundColor: THEME_COLOR, color: 'white' }}
                variant="contained"
                onClick={handleCreate}
              >
                Create Opportunity
              </Button>
            </Grid>
            <Grid item md={6} container justifyContent="flex-end" direction="row">
              <Grid item container md={12} direction="row">
                <Grid item md={12} container justifyContent="flex-end">
                  <h6 style={{ paddingRight: '5px', fontSize: '15px' }}>Search By: </h6>
                  <SearchBox
                    value={queryParams.get('search') || ''}
                    placeholder="Opportunity Name"
                    search={handleSearch}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid paddingX={0} paddingY={1}>
            <TableContainer component={Paper}>
              <Table aria-label="simple table" style={{ tableLayout: 'auto' }}>
                <TableHead>
                  <TableRow>
                    {tableColumns.map((tCol, idx) => (
                      <TableCell key={idx} style={{ fontSize: '14px', fontWeight: 'bold' }}>
                        {tCol}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>{tableContent}</TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      </Grid>

      {showConfirmDialog && (
        <ConfirmDialog
          handleCancel={() => setShowConfirmDialog(false)}
          handleOk={handleDeleteConfirm}
          message="Are you sure you want to delete this opportunity?"
        />
      )}
    </div>
  );
}
